import type { ArtifactValidation, ValidationOutcome } from "@/services/studio/types";

/**
 * Presentation mapping for check-only Metadata API validation outcomes.
 *
 * The backend stopped reporting a check-only deploy as "deployed" and now returns a
 * THIRD outcome, `unvalidated` — the validation could not complete (timeout, queue
 * saturation, auth failure, missing user context, budget exhausted, feature disabled).
 * That is not a failure: rendering it red would tell a user their file is broken when
 * we simply never checked it. It gets its own caution tone, distinct from both the
 * green `validated` and the red `validation_failed`.
 */
export interface ValidationPresentation {
  /** Badge variant from the studio Badge component. */
  tone: "success" | "warm" | "destructive";
  label: string;
  /** Short sentence explaining what the outcome does and does not tell the user. */
  detail: string;
}

const PRESENTATION: Record<ValidationOutcome, ValidationPresentation> = {
  validated: {
    tone: "success",
    label: "Validated with org",
    detail: "Checked against the org — no changes were written.",
  },
  validation_failed: {
    tone: "destructive",
    label: "Validation failed",
    detail: "The org rejected this file. No changes were written.",
  },
  unvalidated: {
    tone: "warm",
    label: "Not validated",
    detail: "The check did not complete, so this file was never verified either way.",
  },
};

/** Narrow an arbitrary backend string to a known outcome, or `null` when unrecognised. */
export function asValidationOutcome(value: unknown): ValidationOutcome | null {
  return value === "validated" || value === "validation_failed" || value === "unvalidated"
    ? value
    : null;
}

export function validationPresentation(outcome: ValidationOutcome): ValidationPresentation {
  return PRESENTATION[outcome];
}

/**
 * Human-readable cause for an `unvalidated` verdict. The backend sends machine reason
 * codes (`poll_timeout`, `submit_rejected_http_400`, …); anything unrecognised is
 * passed through rather than swallowed, so a user always sees WHY.
 */
export function unvalidatedReasonLabel(reason: string | null | undefined): string | null {
  if (!reason) return null;
  if (reason === "poll_timeout" || reason === "validation_timeout") {
    return "The org did not return a result in time.";
  }
  if (reason === "validation_did_not_run") return "The validation never started.";
  if (reason === "no_user_context") return "No Salesforce user context was available.";
  if (reason === "budget_exhausted" || reason === "over_unit_budget") {
    return "The validation budget for this run was exhausted.";
  }
  if (reason.startsWith("submit_rejected_http_")) {
    return `Salesforce rejected the validation request (HTTP ${reason.slice("submit_rejected_http_".length)}).`;
  }
  if (reason.startsWith("error:")) {
    return `The validation errored (${reason.slice("error:".length)}).`;
  }
  return reason;
}

/**
 * Read the outcome off an artifact's `validation` field. Deployable artifacts ALWAYS
 * carry a verdict (including `unvalidated`); non-deployable ones (reports, CSVs) carry
 * `null` and must show no badge at all rather than a misleading neutral one.
 */
export function artifactValidationOutcome(
  validation: ArtifactValidation | null | undefined
): ValidationOutcome | null {
  return validation ? asValidationOutcome(validation.outcome) : null;
}

/**
 * One-line summary of a run-level validation roll-up, e.g. "2 validated · 1 not validated".
 * Returns `null` when nothing was validated, so callers can omit the row entirely.
 */
export function validationCountsSummary(
  counts: { validated: number; validation_failed: number; unvalidated: number } | undefined
): string | null {
  if (!counts) return null;
  const parts: string[] = [];
  if (counts.validated) parts.push(`${counts.validated} validated`);
  if (counts.validation_failed) parts.push(`${counts.validation_failed} failed validation`);
  if (counts.unvalidated) parts.push(`${counts.unvalidated} not validated`);
  return parts.length ? parts.join(" · ") : null;
}
